import {useCallback, useEffect, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger} from '@/components/ui/dropdown-menu'
import {DataTable, type Column} from '@/components/data-table'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {HelpIcon} from '@/components/help-icon'
import {Hint} from '@/components/ui/hint'
import {PorQue} from '@/components/ui/por-que'
import {IngestaBar} from '@/components/bateria/ingesta-barra'
import {formatSize} from '@/hooks/sgsst/evidencias/use-evidence'
import {useFileDrop} from '@/hooks/common/use-file-drop'
import {
  cuarentenaDeArchivo,
  usePrecalentamiento,
  usePrecalentarRecortes,
  useReprocesarCuarentena,
  useUploadScan,
  type IngestaJob,
} from '@/hooks/bateria/use-bateria'
import {useDescartarTanda} from '@/hooks/bateria/use-bateria-tandas'
import {useDescartarHojasDeArchivo, useMoverHojasDeArchivo} from '@/hooks/bateria/use-cuarentena'
import {Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {useEvaluaciones} from '@/hooks/bateria/use-bateria'
import {
  claveDeErrorDeLote,
  displayFilename,
  isZipFile,
  paginasSinDesenlace,
  scanFileError,
  tandaDetenida,
  type LotesResumen,
} from '@/lib/bateria'
import {refsDeUbicar} from '@/lib/bateria-cola'
import {MAX_REFS_EN_URL} from '@/lib/cola-filtros'
import {formatDateTime} from '@/lib/risk'
import {publicarAviso, publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

type EscaneoEstado = 'cola' | 'subiendo' | 'listo' | 'duplicado' | 'error'

type EscaneoRow = {
  key: string
  file: File
  status: EscaneoEstado
  progress: number
  error: string | null
}

// No es regla del backend: no saturar el enlace de subida del operador (§3.6).
const MAX_CONCURRENT = 2

const HISTORIAL_PAGE_SIZE = 20

// Un job en curso no tiene magnitud: no gasta el escalón lime de la rampa en decir «esperá».
const JOB_ESTADO_TONE: Record<IngestaJob['estado'], 'enCurso' | 'green' | 'amber'> = {
  enumerando: 'enCurso',
  procesando: 'enCurso',
  completo: 'green',
  completo_con_pendientes: 'amber',
  descartado: 'amber',
}

function contadoresDe(job: IngestaJob, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const nuevas = Math.max(0, job.paginas_total - job.paginas_duplicadas)
  const partes = [t('admin.bateria.escaneosV2.historial.paginas', {count: job.paginas_total})]
  if (job.paginas_total > 0) partes.push(t('admin.bateria.escaneosV2.historial.nuevas', {count: nuevas}))
  if (job.paginas_duplicadas > 0)
    partes.push(t('admin.bateria.escaneosV2.historial.yaEstaban', {count: job.paginas_duplicadas}))
  if (job.paginas_reemplazadas > 0)
    partes.push(t('admin.bateria.escaneosV2.historial.reemplazos', {count: job.paginas_reemplazadas}))
  if (job.paginas_en_cuarentena > 0)
    partes.push(t('admin.bateria.escaneosV2.historial.sinUbicar', {count: job.paginas_en_cuarentena}))
  // Al final: es el desenlace de la fila (el papel que hay que volver a subir), no un conteo más.
  if (job.paginas_asentadas > 0)
    partes.push(t('admin.bateria.escaneosV2.historial.sinProcesar', {count: job.paginas_asentadas}))
  return partes.join(' · ')
}

/** Sesión de subida efímera (un archivo = una tanda) sobre el historial persistente de tandas; un
 * archivo duplicado entero no crea lote y por eso solo aparece arriba. */
export function EscaneosTab({
  businessId,
  evaluacionId,
  jobs,
  lotes,
}: {
  businessId: string
  evaluacionId: string
  jobs: IngestaJob[]
  lotes: LotesResumen
}) {
  const {t} = useTranslation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadScan(businessId, evaluacionId)
  const descartar = useDescartarTanda(businessId, evaluacionId)
  const descartarHojas = useDescartarHojasDeArchivo(businessId, evaluacionId)
  const moverHojas = useMoverHojasDeArchivo(businessId, evaluacionId)
  // Paginado en el cliente: `jobs` ya llega completo como prop.
  const [historialPage, setHistorialPage] = useState(1)
  const [moviendoHojas, setMoviendoHojas] = useState<IngestaJob | null>(null)
  // Ref de callback: Radix monta el contenido del diálogo después del primer efecto (ver `popover.tsx`).
  const [nodoMover, setNodoMover] = useState<HTMLElement | null>(null)
  const [destinoId, setDestinoId] = useState<string | null>(null)
  const evaluaciones = useEvaluaciones(businessId, 1, moviendoHojas !== null)
  const destinos = (evaluaciones.data?.items ?? []).filter((e) => e.estado === 'abierta' && e.id !== evaluacionId)
  const reprocesar = useReprocesarCuarentena(businessId, evaluacionId)
  const precalentamiento = usePrecalentamiento(businessId, evaluacionId)
  const precalentar = usePrecalentarRecortes(businessId, evaluacionId)
  const recortesFaltantes = precalentamiento.data?.paginas_pendientes ?? 0
  // Leído del servidor: el `isPending` de la mutación se pierde al recargar. Un lote detenido no
  // bloquea, para poder reintentar.
  const enCurso = lotes.corriendo && !lotes.detenido
  const [rows, setRows] = useState<EscaneoRow[]>([])
  const [paused, setPaused] = useState(false)
  const [folderError, setFolderError] = useState(false)
  const [ubicando, setUbicando] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState<IngestaJob | null>(null)
  const [descartandoHojas, setDescartandoHojas] = useState<IngestaJob | null>(null)

  function patchRow(key: string, patch: Partial<EscaneoRow>) {
    setRows((prev) => prev.map((r) => (r.key === key ? {...r, ...patch} : r)))
  }

  const startUpload = useCallback(
    async (row: EscaneoRow) => {
      try {
        // Un PDF suelto va sin manifiesto: sin `paquete_id` se rutea por QR como un ZIP (§3.5).
        const manifest = isZipFile(row.file) ? {} : undefined
        const result = await upload.mutateAsync({
          file: row.file,
          manifest,
          onProgress: (pct) => patchRow(row.key, {progress: pct}),
        })
        patchRow(row.key, {status: result.status === 'duplicate' ? 'duplicado' : 'listo', progress: 100})
      } catch (err) {
        patchRow(row.key, {status: 'error', error: translateApiError(err)})
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `upload` es estable entre renders (react-query)
    []
  )

  // Idempotente: una fila `subiendo` no se rearranca, así que correr en cada tick de progreso es
  // barato. Pausar frena solo el arranque de filas nuevas.
  useEffect(() => {
    if (paused) return
    const activos = rows.filter((r) => r.status === 'subiendo').length
    const cupo = MAX_CONCURRENT - activos
    if (cupo <= 0) return
    const siguientes = rows.filter((r) => r.status === 'cola').slice(0, cupo)
    if (siguientes.length === 0) return
    setRows((prev) => prev.map((r) => (siguientes.some((s) => s.key === r.key) ? {...r, status: 'subiendo'} : r)))
    for (const row of siguientes) startUpload(row)
  }, [rows, paused, startUpload])

  function enqueue(files: File[]) {
    setFolderError(false)
    const nuevas: EscaneoRow[] = files.map((file) => {
      const problema = scanFileError(file)
      return {
        key: `${file.name}-${Date.now()}-${Math.random()}`,
        file,
        status: problema ? 'error' : 'cola',
        progress: 0,
        error: problema ? t(`admin.bateria.escaneosV2.error.${problema}`) : null,
      }
    })
    setRows((prev) => [...prev, ...nuevas])
  }

  const {active, zoneProps} = useFileDrop({onFiles: enqueue, onFolderRejected: () => setFolderError(true)})

  function retry(row: EscaneoRow) {
    patchRow(row.key, {status: 'cola', progress: 0, error: null})
  }

  function dismiss(key: string) {
    setRows((prev) => prev.filter((r) => r.key !== key))
  }

  const hayEnCola = rows.some((r) => r.status === 'cola' || r.status === 'subiendo')

  // Pide la cuarentena solo al pulsar. Si no cabe en la URL (`MAX_REFS_EN_URL`), abre todo lo sin
  // ubicar del lote y lo avisa, en vez de recortar en silencio.
  async function ubicarTanda(job: IngestaJob) {
    const sourceObjectKey = job.object_keys[0] ?? ''
    const rutaCola = `/admin/bateria/empresas/${businessId}/evaluaciones/${evaluacionId}/revision`
    setUbicando(job.id)
    try {
      const pagina = await cuarentenaDeArchivo(businessId, evaluacionId, sourceObjectKey, MAX_REFS_EN_URL)
      if (pagina.total > MAX_REFS_EN_URL) {
        navigate(`${rutaCola}?ir_a=ubicar`)
        publicarAviso(
          t('admin.bateria.escaneosV2.historial.ubicarTopeTitulo'),
          t('admin.bateria.escaneosV2.historial.ubicarTopeCuerpo', {count: pagina.total}),
          {tipo: 'exito'}
        )
        return
      }
      const refs = refsDeUbicar(pagina.items.map((c) => c.id))
      navigate(`${rutaCola}?solo=${refs.join(',')}`)
    } catch (err) {
      publicarAviso(t('admin.bateria.escaneosV2.historial.ubicarErrorTitulo'), translateApiError(err))
    } finally {
      setUbicando(null)
    }
  }

  const jobColumns: Column<IngestaJob>[] = [
    {
      key: 'archivo',
      header: t('admin.bateria.escaneosV2.historial.archivo'),
      cell: (job) => (
        <span className="block max-w-[16rem] truncate font-mono text-xs text-foreground">
          {displayFilename(job.object_keys[0] ?? '')}
        </span>
      ),
    },
    {
      key: 'cuando',
      header: t('admin.bateria.escaneosV2.historial.cuando'),
      width: 'w-28',
      cell: (job) => <span className="font-mono text-xs text-muted-foreground">{formatDateTime(job.created_at)}</span>,
    },
    {
      key: 'quien',
      header: t('admin.bateria.escaneosV2.historial.quien'),
      width: 'w-36',
      // `block` + `break-words`: `truncate` sobre un inline no recorta y el nombre invadía la celda vecina.
      cell: (job) => <span className="block text-sm break-words text-foreground">{job.lanzado_por_name ?? '—'}</span>,
    },
    {
      key: 'conteos',
      header: t('admin.bateria.escaneosV2.historial.conteos'),
      cell: (job) =>
        job.error ? (
          <span className="text-sm text-destructive" data-testid={`ingesta-historial-motivo-${job.id}`}>
            {t(claveDeErrorDeLote(job.error))}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{contadoresDe(job, t)}</span>
        ),
    },
    {
      key: 'estado',
      header: t('admin.bateria.escaneosV2.historial.estado'),
      width: 'w-32',
      cell: (job) => (
        <Tag tone={job.error ? 'red' : JOB_ESTADO_TONE[job.estado]} data-testid={`ingesta-historial-estado-${job.id}`}>
          {job.error
            ? t('admin.bateria.escaneosV2.historial.estadoError')
            : t(`admin.bateria.ingesta.estado.${job.estado}`)}
        </Tag>
      ),
    },
    {
      key: 'acciones',
      header: '',
      width: 'w-24',
      className: 'text-right',
      // R2: una sola acción visible, la más urgente; el resto va al kebab.
      cell: (job) => {
        const detenida = tandaDetenida(job, Date.now())
        const primaria: 'ubicar' | 'detenida' | 'error' | null =
          job.paginas_en_cuarentena > 0 ? 'ubicar' : detenida ? 'detenida' : job.error ? 'error' : null
        const secundarias: {key: string; testId: string; label: string; onSelect: () => void}[] = []
        if (job.paginas_en_cuarentena > 0) {
          if (primaria !== 'ubicar') {
            secundarias.push({
              key: 'ubicar',
              testId: `ingesta-historial-ubicar-${job.id}`,
              label: t('admin.bateria.escaneosV2.historial.ubicarHojas', {count: job.paginas_en_cuarentena}),
              onSelect: () => ubicarTanda(job),
            })
          }
          secundarias.push({
            key: 'descartar-hojas',
            testId: `ingesta-historial-descartar-hojas-${job.id}`,
            label: t('admin.bateria.cuarentena.descartarArchivo', {count: job.paginas_en_cuarentena}),
            onSelect: () => setDescartandoHojas(job),
          })
          secundarias.push({
            key: 'mover-hojas',
            testId: `ingesta-historial-mover-hojas-${job.id}`,
            label: t('admin.bateria.cuarentena.moverArchivo', {count: job.paginas_en_cuarentena}),
            onSelect: () => {
              setDestinoId(null)
              setMoviendoHojas(job)
            },
          })
        }
        // Solo con la tanda quieta: sobre una que avanza el servidor contesta 409.
        if (detenida && primaria !== 'detenida') {
          secundarias.push({
            key: 'detenida',
            testId: `ingesta-historial-descartar-${job.id}`,
            label: t('admin.bateria.escaneosV2.historial.descartar'),
            onSelect: () => setConfirmando(job),
          })
        }
        if (job.error && primaria !== 'error') {
          secundarias.push({
            key: 'reintentar',
            testId: `ingesta-historial-reintentar-${job.id}`,
            label: t('admin.bateria.escaneosV2.historial.reintentar'),
            onSelect: () => inputRef.current?.click(),
          })
        }
        return (
          <div className="flex items-center justify-end gap-1">
            {primaria === 'ubicar' && (
              <Button
                size="xs"
                variant="ghost"
                data-testid={`ingesta-historial-ubicar-${job.id}`}
                disabled={ubicando === job.id}
                onClick={() => ubicarTanda(job)}>
                {t('admin.bateria.escaneosV2.historial.ubicarHojas', {count: job.paginas_en_cuarentena})}
              </Button>
            )}
            {primaria === 'detenida' && (
              <Button
                size="xs"
                variant="outline"
                data-testid={`ingesta-historial-descartar-${job.id}`}
                onClick={() => setConfirmando(job)}>
                {t('admin.bateria.escaneosV2.historial.descartar')}
              </Button>
            )}
            {primaria === 'error' && (
              <Button
                size="xs"
                variant="outline"
                data-testid={`ingesta-historial-reintentar-${job.id}`}
                onClick={() => inputRef.current?.click()}>
                {t('admin.bateria.escaneosV2.historial.reintentar')}
              </Button>
            )}
            {job.error && (
              <HelpIcon
                label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneosV2.historial.reintentar')})}
                testId={`ingesta-historial-partir-${job.id}`}>
                {t('admin.bateria.escaneosV2.historial.comoPartirlo')}
              </HelpIcon>
            )}
            {secundarias.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    data-testid={`ingesta-historial-mas-${job.id}`}
                    aria-label={t('admin.bateria.escaneosV2.historial.masAcciones')}>
                    <span aria-hidden="true" className="text-base leading-none">
                      ⋯
                    </span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {secundarias.map((accion) => (
                    <DropdownMenuItem key={accion.key} data-testid={accion.testId} onSelect={accion.onSelect}>
                      {accion.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-4" data-testid="escaneos-tab">
      <section
        {...zoneProps}
        data-testid="escaneos-dropzone"
        data-active={active || undefined}
        className={cn(
          'surface flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors',
          active ? 'border-primary bg-accent/40' : 'border-border'
        )}>
        <p className="text-sm font-medium text-foreground">{t('admin.bateria.escaneosV2.dropzone.titulo')}</p>
        <Button size="sm" data-testid="escaneos-elegir-archivos" onClick={() => inputRef.current?.click()}>
          {t('admin.bateria.escaneosV2.dropzone.elegir')}
        </Button>
        {folderError && (
          <p className="text-xs text-destructive" data-testid="escaneos-carpeta-error">
            {t('admin.bateria.escaneosV2.dropzone.carpetaRechazada')}
          </p>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/zip,.zip,application/pdf,.pdf"
          hidden
          data-testid="escaneos-input"
          onChange={(e) => {
            if (e.target.files) enqueue(Array.from(e.target.files))
            e.target.value = ''
          }}
        />
      </section>

      {rows.length > 0 && (
        <section className="surface space-y-2 rounded-lg p-3" data-testid="escaneos-cola">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.escaneosV2.cola.titulo')}
            </span>
            {hayEnCola && (
              <Button size="xs" variant="outline" data-testid="escaneos-pausar" onClick={() => setPaused((p) => !p)}>
                {paused ? t('admin.bateria.escaneosV2.cola.reanudar') : t('admin.bateria.escaneosV2.cola.pausar')}
              </Button>
            )}
          </div>
          {paused && (
            <p className="tone-amber rounded-md px-3 py-1.5 text-xs" data-testid="escaneos-pausado-aviso">
              {t('admin.bateria.escaneosV2.cola.pausadoAyuda')}
            </p>
          )}
          <ul className="space-y-1.5">
            {rows.map((row) => (
              <li
                key={row.key}
                className="flex items-center gap-3 rounded-md border border-border px-3 py-2"
                data-testid={`escaneos-fila-${row.key}`}>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-foreground">{row.file.name}</p>
                  <p className="font-mono text-[11px] text-muted-foreground">{formatSize(row.file.size)}</p>
                </div>
                {row.status === 'subiendo' && (
                  <div className="h-1.5 w-24 shrink-0 overflow-hidden rounded-full bg-secondary">
                    <div
                      className="h-full rounded-full bg-primary transition-all"
                      style={{width: `${row.progress}%`}}
                    />
                  </div>
                )}
                <span className="shrink-0 text-xs text-muted-foreground">
                  {row.status === 'cola' &&
                    (paused ? t('admin.bateria.escaneosV2.cola.enPausa') : t('admin.bateria.escaneosV2.cola.enCola'))}
                  {row.status === 'listo' && <Tag tone="green">{t('admin.bateria.escaneosV2.cola.listo')}</Tag>}
                  {row.status === 'duplicado' && (
                    <span className="inline-flex items-center gap-1.5">
                      <Tag tone="amber">{t('admin.bateria.escaneosV2.cola.duplicado')}</Tag>
                      <HelpIcon
                        label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneosV2.cola.duplicado')})}
                        testId={`escaneos-duplicado-ayuda-${row.key}`}>
                        {t('admin.bateria.escaneosV2.cola.duplicadoAyuda')}
                      </HelpIcon>
                    </span>
                  )}
                  {row.status === 'error' && <span className="text-destructive">{row.error}</span>}
                </span>
                {row.status === 'error' && (
                  <Button
                    size="xs"
                    variant="outline"
                    data-testid={`escaneos-reintentar-${row.key}`}
                    onClick={() => retry(row)}>
                    {t('admin.bateria.escaneosV2.cola.reintentar')}
                  </Button>
                )}
                {row.status !== 'subiendo' && row.status !== 'cola' && (
                  <Button
                    size="icon-xs"
                    variant="ghost"
                    aria-label={t('admin.bateria.escaneosV2.cola.quitar')}
                    data-testid={`escaneos-quitar-${row.key}`}
                    onClick={() => dismiss(row.key)}>
                    ×
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Solo aparece si faltan recortes: lotes leídos antes del pre-corte o un hueco que reparar. */}
      {recortesFaltantes > 0 && (
        <section
          className="surface flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3"
          data-testid="precalentar-recortes">
          <Button
            size="sm"
            variant="outline"
            data-testid="precalentar-recortes-boton"
            disabled={precalentar.isPending}
            onClick={() =>
              precalentar.mutate(undefined, {
                onSuccess: (r) =>
                  publicarExito(
                    t('admin.bateria.precalentar.listoTitulo'),
                    t('admin.bateria.precalentar.listoCuerpo', {count: r.paginas_encoladas})
                  ),
              })
            }>
            {precalentar.isPending
              ? t('admin.bateria.precalentar.enCurso')
              : t('admin.bateria.precalentar.boton', {count: recortesFaltantes})}
          </Button>
        </section>
      )}

      <ListCard
        title={t('admin.bateria.escaneosV2.historial.titulo')}
        actions={
          lotes.sinUbicar > 0 && (
            <div className="flex max-w-xs flex-col items-start gap-1 sm:items-end sm:text-right">
              <Button
                size="sm"
                variant="outline"
                data-testid="cuarentena-reprocesar"
                disabled={reprocesar.isPending || enCurso}
                onClick={() => reprocesar.mutate()}>
                {enCurso ? t('admin.bateria.cuarentena.reprocesarEnCurso') : t('admin.bateria.cuarentena.reprocesar')}
              </Button>
              {enCurso && (
                <Hint testId="cuarentena-reprocesar-ayuda">
                  {t('admin.bateria.cuarentena.reprocesarEnCursoAyuda', {
                    leidas: lotes.leidas,
                    total: lotes.paginasDelArchivo,
                  })}
                </Hint>
              )}
              {reprocesar.isSuccess && (
                <p className="text-xs text-muted-foreground" data-testid="cuarentena-reprocesar-resultado">
                  {t('admin.bateria.cuarentena.reprocesarResultado', {count: reprocesar.data.paginas})}
                </p>
              )}
              {reprocesar.isError && (
                <p className="text-xs text-destructive" data-testid="cuarentena-reprocesar-error">
                  {translateApiError(reprocesar.error)}
                </p>
              )}
            </div>
          )
        }
        footer={
          jobs.length > HISTORIAL_PAGE_SIZE && (
            <PaginationBar
              page={historialPage}
              pageSize={HISTORIAL_PAGE_SIZE}
              total={jobs.length}
              onPageChange={setHistorialPage}
            />
          )
        }>
        {jobs.length === 0 ? (
          <ListEmptyState title={t('admin.bateria.escaneosV2.historial.emptyTitle')} />
        ) : (
          <DataTable
            columns={jobColumns}
            rows={jobs.slice((historialPage - 1) * HISTORIAL_PAGE_SIZE, historialPage * HISTORIAL_PAGE_SIZE)}
            rowKey={(j) => j.id}
            renderRowDetail={(job) => <IngestaBar job={job} />}
          />
        )}
        {descartar.isError && (
          <p className="px-5 pb-3 text-sm text-destructive" data-testid="ingesta-descartar-error">
            {translateApiError(descartar.error)}
          </p>
        )}
      </ListCard>

      <Dialog open={moviendoHojas !== null} onOpenChange={(open) => !open && setMoviendoHojas(null)}>
        <DialogContent ref={setNodoMover} data-testid="ingesta-mover-hojas-dialogo">
          <DialogHeader>
            <DialogTitle>{t('admin.bateria.cuarentena.moverArchivoTitulo')}</DialogTitle>
            <DialogDescription>
              {t('admin.bateria.cuarentena.moverArchivoCuerpo', {
                count: moviendoHojas?.paginas_en_cuarentena ?? 0,
                archivo: displayFilename(moviendoHojas?.object_keys[0] ?? ''),
              })}
            </DialogDescription>
          </DialogHeader>
          {/* `contenedor` monta el popover dentro del diálogo: el primer Escape cierra solo el
              popover (ver `popover.tsx`). */}
          <PorQue
            label={t('admin.bateria.cuarentena.moverArchivoPorqueLabel')}
            contenedor={nodoMover}
            testId="ingesta-mover-hojas-porque"
            contenidoTestId="ingesta-mover-hojas-porque-contenido">
            {t('admin.bateria.cuarentena.moverArchivoPorque')}
          </PorQue>
          {/* Botones de una columna, no un desplegable nativo (doctrina R3). */}
          <div className="space-y-2" data-testid="ingesta-mover-hojas-destinos">
            <p className="font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
              {t('admin.bateria.cuarentena.moverArchivoDestino')}
            </p>
            {destinos.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('admin.bateria.cuarentena.moverArchivoSinDestino')}</p>
            ) : (
              destinos.map((e) => (
                <Button
                  key={e.id}
                  type="button"
                  variant={destinoId === e.id ? 'default' : 'outline'}
                  className="w-full justify-start"
                  data-testid={`ingesta-mover-hojas-destino-${e.id}`}
                  onClick={() => setDestinoId(e.id)}>
                  {formatDateTime(e.fecha_inicio)} · {e.psicologo_name ?? '—'}
                </Button>
              ))
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => setMoviendoHojas(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              data-testid="ingesta-mover-hojas-confirmar"
              disabled={!destinoId || moverHojas.isPending}
              onClick={() => {
                if (!moviendoHojas || !destinoId) return
                moverHojas.mutate(
                  {sourceObjectKey: moviendoHojas.object_keys[0] ?? '', destinoEvaluacionId: destinoId},
                  {
                    onSuccess: (resultado) =>
                      publicarExito(
                        t('admin.bateria.cuarentena.moverArchivoListoTitulo'),
                        t('admin.bateria.cuarentena.moverArchivoListoCuerpo', {count: resultado.movidas})
                      ),
                    onSettled: () => setMoviendoHojas(null),
                  }
                )
              }}>
              {t('admin.bateria.cuarentena.moverArchivoConfirmar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={descartandoHojas !== null}
        onOpenChange={(open) => !open && setDescartandoHojas(null)}
        title={t('admin.bateria.cuarentena.descartarArchivoTitulo')}
        description={t('admin.bateria.cuarentena.descartarArchivoCuerpo', {
          count: descartandoHojas?.paginas_en_cuarentena ?? 0,
          archivo: displayFilename(descartandoHojas?.object_keys[0] ?? ''),
        })}
        confirmLabel={t('admin.bateria.cuarentena.descartarArchivoConfirmar')}
        cancelLabel={t('common.cancel')}
        pending={descartarHojas.isPending}
        onConfirm={() => {
          if (!descartandoHojas) return
          descartarHojas.mutate(
            {sourceObjectKey: descartandoHojas.object_keys[0] ?? ''},
            {
              onSuccess: (resultado) =>
                publicarExito(
                  t('admin.bateria.cuarentena.descartarArchivoListoTitulo'),
                  t('admin.bateria.cuarentena.descartarArchivoListoCuerpo', {count: resultado.descartadas})
                ),
              onSettled: () => setDescartandoHojas(null),
            }
          )
        }}
      />

      <ConfirmDialog
        open={confirmando !== null}
        onOpenChange={(open) => !open && setConfirmando(null)}
        title={t('admin.bateria.escaneosV2.descartar.titulo')}
        description={t('admin.bateria.escaneosV2.descartar.cuerpo', {
          count: confirmando ? paginasSinDesenlace(confirmando) : 0,
          archivo: displayFilename(confirmando?.object_keys[0] ?? ''),
        })}
        confirmLabel={t('admin.bateria.escaneosV2.descartar.confirmar')}
        cancelLabel={t('common.cancel')}
        pending={descartar.isPending}
        onConfirm={() => {
          if (!confirmando) return
          const perdidas = paginasSinDesenlace(confirmando)
          descartar.mutate(confirmando.id, {
            onSuccess: () =>
              publicarExito(
                t('admin.bateria.escaneosV2.descartar.listoTitulo'),
                t('admin.bateria.escaneosV2.descartar.listoCuerpo', {count: perdidas})
              ),
            onSettled: () => setConfirmando(null),
          })
        }}
      />
    </div>
  )
}
